/**
 * Multi-Policy Loan Snowball Engine
 * 
 * Simulates all possible payoff orders across multiple policy loans
 * and selects the order that minimizes total interest paid to the life company.
 */

import {
  parseDate,
  toISODate,
  diffInDays,
  daysInYear,
  getPaymentsPerYear,
  getNextPaymentDate,
  type PaymentFrequency,
} from "./calculationEngine";

// ─── Types ───────────────────────────────────────────────────

export interface PolicyLoanEntry {
  id: string;           // e.g. "policy-1"
  label: string;        // e.g. "Policy 1"
  amount: number;
  startDate: string;    // ISO date
  policyNumber: string;
  interestRate: number; // annual decimal, e.g. 0.065
}

export interface SnowballInput {
  policies: PolicyLoanEntry[];
  paymentAmount: number;
  paymentFrequency: PaymentFrequency;
  firstPaymentDate: string;
  policyLoanRate: number; // annual decimal — used if individual rates not set
}

export interface SnowballPhase {
  policyId: string;
  policyLabel: string;
  policyNumber: string;
  startingBalance: number;
  paymentsInPhase: number;
  interestInPhase: number;
  payoffPaymentNumber: number; // overall payment # when this policy is paid off
}

export interface SnowballResult {
  phases: SnowballPhase[];
  totalInterest: number;
  totalPayments: number;
  orderDescription: string; // e.g. "Policy 3 → Policy 1 → Policy 2"
}

export interface SnowballAnalysis {
  optimal: SnowballResult;
  allResults: SnowballResult[];
  paymentAmount: number;
  paymentFrequency: PaymentFrequency;
  firstPaymentDate: string;
}

// ─── Helpers ─────────────────────────────────────────────────

function permutations<T>(arr: T[]): T[][] {
  if (arr.length <= 1) return [arr];
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i++) {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    for (const perm of permutations(rest)) {
      result.push([arr[i], ...perm]);
    }
  }
  return result;
}

/**
 * Simulate snowball payoff for a given ordering of policies.
 * All payments go to the first policy; once paid off, roll to next.
 * Interest accrues on all outstanding policies each period.
 */
function simulateOrder(
  order: PolicyLoanEntry[],
  paymentAmount: number,
  paymentFrequency: PaymentFrequency,
  firstPaymentDate: string,
): SnowballResult {
  const ppy = getPaymentsPerYear(paymentFrequency);
  
  // Track balances
  const balances = new Map<string, number>();
  for (const p of order) {
    balances.set(p.id, p.amount);
  }

  const phases: SnowballPhase[] = [];
  let totalInterest = 0;
  let paymentNumber = 0;
  let currentPhaseIndex = 0;
  let phasePaymentCount = 0;
  let phaseInterest = 0;
  const maxPayments = 1200; // safety cap: 100 years weekly

  while (currentPhaseIndex < order.length && paymentNumber < maxPayments) {
    paymentNumber++;
    
    // 1. Accrue interest on ALL outstanding policies
    let periodInterest = 0;
    for (const p of order) {
      const bal = balances.get(p.id)!;
      if (bal > 0.01) {
        const interest = bal * p.interestRate / ppy;
        periodInterest += interest;
        balances.set(p.id, bal + interest);
      }
    }
    totalInterest += periodInterest;
    phaseInterest += periodInterest;

    // 2. Apply full payment to the current target policy
    let remaining = paymentAmount;
    
    // First pay the current target
    while (currentPhaseIndex < order.length && remaining > 0.01) {
      const target = order[currentPhaseIndex];
      const bal = balances.get(target.id)!;
      
      if (bal <= 0.01) {
        // This policy is already paid off, record phase and move on
        if (phasePaymentCount > 0) {
          phases.push({
            policyId: target.id,
            policyLabel: target.label,
            policyNumber: target.policyNumber,
            startingBalance: target.amount,
            paymentsInPhase: phasePaymentCount,
            interestInPhase: phaseInterest,
            payoffPaymentNumber: paymentNumber,
          });
        }
        currentPhaseIndex++;
        phasePaymentCount = 0;
        phaseInterest = 0;
        continue;
      }

      const applied = Math.min(remaining, bal);
      balances.set(target.id, bal - applied);
      remaining -= applied;

      if (balances.get(target.id)! <= 0.01) {
        // Policy paid off
        balances.set(target.id, 0);
        phasePaymentCount++;
        phases.push({
          policyId: target.id,
          policyLabel: target.label,
          policyNumber: target.policyNumber,
          startingBalance: target.amount,
          paymentsInPhase: phasePaymentCount,
          interestInPhase: phaseInterest,
          payoffPaymentNumber: paymentNumber,
        });
        currentPhaseIndex++;
        phasePaymentCount = 0;
        phaseInterest = 0;
      } else {
        phasePaymentCount++;
        break; // remaining payment used up
      }
    }
  }

  const orderDescription = order.map(p => p.label + (p.policyNumber ? ` (#${p.policyNumber})` : '')).join(' → ');

  return {
    phases,
    totalInterest,
    totalPayments: paymentNumber,
    orderDescription,
  };
}

// ─── Main API ────────────────────────────────────────────────

export function runSnowballAnalysis(input: SnowballInput): SnowballAnalysis {
  // Assign individual rates if not set
  const policies = input.policies.map(p => ({
    ...p,
    interestRate: p.interestRate || input.policyLoanRate,
  }));

  const allOrders = permutations(policies);
  const allResults: SnowballResult[] = [];

  for (const order of allOrders) {
    const result = simulateOrder(
      order,
      input.paymentAmount,
      input.paymentFrequency,
      input.firstPaymentDate,
    );
    allResults.push(result);
  }

  // Find optimal (lowest total interest)
  allResults.sort((a, b) => a.totalInterest - b.totalInterest);
  const optimal = allResults[0];

  return {
    optimal,
    allResults,
    paymentAmount: input.paymentAmount,
    paymentFrequency: input.paymentFrequency,
    firstPaymentDate: input.firstPaymentDate,
  };
}
